<template>
  <div>
    <div class="grid grid-cols-1 gap-4 max-h-[60vh] overflow-y-auto">
      <div class="mt-2 px-8">
        <div
          v-bind:class="{ 'border border-gray-400 py-3 rounded-md': !isResultView }"
          class="grid grid-cols-2 gap-4 px-2 mb-8"
        >
          <h2 class="text-lg font-semibold text-gray-600">Test Results</h2>
          <div class="flex justify-end">
            <button
              @click="hideResultView"
              v-show="!isResultView"
              class="px-4 py-1 text-custom-blue rounded hover:text-custom-yellow"
            >
              Show All Results
            </button>
          </div>
        </div>

        <!-- ── Overview table ── -->
        <div v-show="isResultView">
          <table class="min-w-full divide-y divide-gray-200 border-x-2">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-4 py-3 text-left font-medium text-gray-600">Test</th>
                <th scope="col" class="px-4 py-3 text-left font-medium text-gray-600">Reports</th>
                <th scope="col" class="px-4 py-3 text-left font-medium text-gray-600">
                  Collected By
                </th>
                <th scope="col" class="px-4 py-3 text-left font-medium text-gray-600">
                  Conducted By
                </th>
                <th scope="col" class="px-4 py-3 text-left font-medium text-gray-600">
                  Reviewed By
                </th>
                <th scope="col" class="px-4 py-3 text-left font-medium text-gray-600">Ref. Ranges / Results</th>
                <th scope="col" class="px-4 py-3 text-left font-medium text-gray-600">Comments</th>
                <th scope="col" class="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                <th scope="col" class="px-4 py-3 text-left font-medium text-gray-600"></th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(test, index) in filteredRequests" :key="index">
                <td class="px-4 py-2 whitespace-nowrap">{{ test.test_type?.name }}</td>
                <td class="px-4 py-2 whitespace-nowrap">
                  <span
                    v-for="(file, fileIndex) in test.lab_results[0]?.url"
                    :key="fileIndex"
                    class="jiji_blue cursor-pointer block"
                    @click="downloadFile(file.url)"
                  >{{ file.filename || 'Download Report' }}</span>
                  <span v-if="!test.lab_results[0]?.url || test.lab_results[0]?.url.length === 0">No Attachment</span>
                </td>

                <!-- Who collected the sample -->
                <td class="px-4 py-2 whitespace-nowrap text-sm">
                  <span v-if="test.collecter?.Username" class="text-gray-800">
                    {{ test.collecter.Username }}
                  </span>
                  <span v-else class="text-gray-400 italic">—</span>
                </td>

                <!-- Who conducted the test (filled_by on the result record) -->
                <td class="px-4 py-2 whitespace-nowrap text-sm">
                  <span v-if="test.lab_results[0]?.filled_by?.Username" class="text-gray-800">
                    {{ test.lab_results[0].filled_by.Username }}
                  </span>
                  <span v-else class="text-gray-400 italic">—</span>
                </td>

                <!-- Who reviewed & published (reviewer = updated_by on lab_test) -->
                <td class="px-4 py-2 whitespace-nowrap text-sm">
                  <span v-if="test.reviewer?.Username" class="text-gray-800">
                    {{ test.reviewer.Username }}
                  </span>
                  <span v-else-if="test.status?.code === 'ST-013'" class="text-gray-400 italic">
                    Not recorded
                  </span>
                  <span v-else class="text-gray-400 italic">—</span>
                </td>

                <td class="px-4 py-2">
                  <div
                    v-for="(result, vi) in [...test.lab_results].reverse()"
                    :key="vi"
                    class="mb-2"
                  >
                    <span class="text-xs font-semibold text-blue-600 mr-1">
                      {{ vi === 0 ? 'Latest:' : `Amend ${test.lab_results.length - 1 - vi}:` }}
                    </span>
                    <div v-if="parseAnalyteBlob(result.results)" class="space-y-0.5 mt-0.5">
                      <div v-for="(entry, name) in parseAnalyteBlob(result.results)" :key="name" class="text-xs whitespace-nowrap">
                        <span class="font-medium text-gray-700">{{ name }}:</span>
                        <span :class="entry.flag === 'H' || entry.flag === 'L' ? 'font-semibold text-red-700' : 'text-gray-800'">{{ entry.value }}</span>
                        <span v-if="entry.unit" class="text-gray-400">{{ entry.unit }}</span>
                        <span v-if="entry.flag === 'H' || entry.flag === 'L'" class="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-700">{{ entry.flag }}</span>
                      </div>
                    </div>
                    <span v-else class="text-sm">{{ result.results }}</span>
                  </div>
                </td>
                <td class="px-4 py-2">
                  <div
                    v-for="(result, vi) in [...test.lab_results].reverse()"
                    :key="vi"
                    class="text-sm text-gray-500"
                  >
                    {{ result.comments || '—' }}
                  </div>
                </td>
                <td class="px-4 py-2 whitespace-nowrap">{{ test.status?.name }}</td>
                <td class="px-4 py-2 whitespace-nowrap">
                  <div class="flex space-x-4">
                    <button
                      @click="showResultView(test)"
                      class="px-4 py-1 bg-custom-yellow text-white rounded hover:bg-yellow-600"
                    >
                      View
                    </button>
                    <button
                      v-if="test.analyte_results?.length"
                      @click="openReportModal(test)"
                      class="ml-2 px-4 py-1 border border-blue-600 text-blue-600 rounded hover:bg-blue-50 text-xs"
                    >
                      Print Report
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Review & publish form -->
          <div class="grid grid-cols-1 gap-6 mt-8">
            <div v-if="!reviewComplete" class="mb-0">
              <h3 class="text-md font-medium text-gray-600">Review Comments:</h3>
              <textarea
                v-model="reviewForm.comment"
                rows="3"
                placeholder="Type..."
                class="w-full border-gray-300 rounded p-2 bg-gray-50 mt-2"
              ></textarea>
            </div>
            <div class="mt-4 px-8 flex justify-end">
              <button
                v-if="!reviewComplete"
                :disabled="publishing"
                @click="submitReviewedResults"
                class="px-4 py-2 bg-custom-yellow text-white rounded hover:bg-yellow-600"
              >
                {{ publishing ? "Reviewing..." : "Review & Publish" }}
              </button>
            </div>
          </div>
        </div>

        <!-- ── Detail view (single test) ── -->
        <div v-show="!isResultView" class="grid grid-cols-2 gap-6 mt-4">
          <div>
            <div class="mb-4">
              <h3 class="text-md font-medium text-gray-600 mt-2">Lab Request ID:</h3>
              <p class="text-md mt-2">{{ selectedTest?.id || 'N/A' }}</p>
            </div>
            <div class="mb-4">
              <h3 class="text-md font-medium text-gray-600 mt-2">Patient ID:</h3>
              <p class="text-md mt-2">{{ selectedTest?.patient_id || 'N/A' }}</p>
            </div>
            <div class="mb-4">
              <h3 class="text-md font-medium text-gray-600 mt-2">Date &amp; Time Reviewed:</h3>
              <p class="text-md mt-2">{{ formattedTimestamp(selectedTest?.updated_at) || 'N/A' }}</p>
            </div>
          </div>

          <div>
            <div class="mb-4">
              <h3 class="text-md font-medium text-gray-600 mt-2">Result History:</h3>
              <div v-if="selectedTest?.lab_results?.length" class="space-y-2 mt-2">
                <div
                  v-for="(result, vi) in [...selectedTest.lab_results].reverse()"
                  :key="vi"
                  class="border border-gray-100 rounded p-2 bg-gray-50"
                >
                  <div class="flex items-center justify-between mb-1">
                    <span class="text-xs font-semibold text-blue-600">
                      {{ vi === 0 ? 'Latest Entry' : `Amendment ${selectedTest.lab_results.length - 1 - vi}` }}
                    </span>
                    <span class="text-xs text-gray-400">
                      {{ result.created_at ? new Date(result.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '' }}
                      <span v-if="result.filled_by"> · {{ result.filled_by.Username }}</span>
                    </span>
                  </div>
                  <div v-if="parseAnalyteBlob(result.results)" class="space-y-0.5">
                    <div v-for="(entry, name) in parseAnalyteBlob(result.results)" :key="name" class="text-xs">
                      <span class="font-medium text-gray-700">{{ name }}:</span>
                      <span :class="entry.flag === 'H' || entry.flag === 'L' ? 'font-semibold text-red-700' : 'text-gray-800'">{{ entry.value }}</span>
                      <span v-if="entry.unit" class="text-gray-400">{{ entry.unit }}</span>
                      <span v-if="entry.flag === 'H' || entry.flag === 'L'" class="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-700">{{ entry.flag }}</span>
                    </div>
                  </div>
                  <p v-else class="text-sm text-gray-800">{{ result.results || '—' }}</p>
                  <p v-if="result.comments" class="text-xs text-gray-500 italic mt-0.5">{{ result.comments }}</p>
                </div>
              </div>
              <p v-else class="text-sm text-gray-400 mt-2">No results entered yet.</p>
            </div>

            <!-- Chain of custody -->
            <div class="mb-4 p-3 bg-blue-50 rounded-md border border-blue-100">
              <h3 class="text-sm font-semibold text-blue-700 mb-2">Lab Chain of Custody</h3>
              <div class="space-y-1 text-sm">
                <div class="flex items-center gap-2">
                  <span class="w-32 text-gray-500">Collected By:</span>
                  <span class="font-medium">{{ selectedTest?.collecter?.Username || 'Not recorded' }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="w-32 text-gray-500">Conducted By:</span>
                  <span class="font-medium">{{ selectedTest?.lab_results[0]?.filled_by?.Username || 'Not recorded' }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="w-32 text-gray-500">Reviewed By:</span>
                  <span class="font-medium">{{ selectedTest?.reviewer?.Username || 'Not recorded' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <LabReportModal
  :show="showReportModal"
  :lab-test="reportLabTest || {}"
  :patient="patient"
  :visit="visit"
  @close="showReportModal = false; reportLabTest = null"
  />
</template>

<script>
import axios from "axios";
import apiClient from "@/services/api";
import LabReportModal from "@/views/modals/Laboratory/LabReportModal.vue";

export default {
  name: "ReviewResultsTab",
  components: {
    LabReportModal
  },
  props: {
    requests: {
      type: Array,
      required: true,
    },
    patient: { type: Object, default: () => ({}) },
    visit:   { type: Object, default: () => ({}) },
  },
  data() {
    return {
      isResultView: true,
      publishing: false,
      reviewComplete: false,
      reviewForm: {
        comment: "",
      },
      selectedTest: null,
      showReportModal: false,
      reportLabTest: null,
    };
  },
  watch: {
    requests: {
      immediate: true,
      handler(requests) {
        const totalItems = requests.length;
        const publishedItems = requests.filter(item => item.status?.code === "ST-013").length;
        this.reviewComplete = totalItems === publishedItems;
      },
    },
  },
  mounted() {
    this.$emit("mounted", "ReviewResultsTab");
  },
  computed: {
    filteredRequests() {
      return this.requests.filter(
        item => item.status?.code === "ST-003" || item.status?.code === "ST-013"
      );
    },
  },
  methods: {
    // Structured lab capture stores results as a JSON blob (`{ "Analyte": { value, unit, flag } }`);
    // older free-text results aren't JSON, so this returns null and the raw string is shown instead.
    parseAnalyteBlob(resultString) {
      if (!resultString || typeof resultString !== 'string') return null;
      try {
        const parsed = JSON.parse(resultString);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length) {
          return parsed;
        }
        return null;
      } catch {
        return null;
      }
    },

    openReportModal(test) {
      this.reportLabTest = test;
      this.showReportModal = true;
    },
    showResultView(test) {
      this.isResultView = false;
      this.selectedTest = test;
    },
    hideResultView() {
      this.isResultView = true;
      this.selectedTest = null;
    },
    submitLabPayload() {
      return {
        ids: this.extractIds(),
        ...this.reviewForm,
        // userId is automatically injected by fetchApi from localStorage
      };
    },
    async submitReviewedResults() {
      this.publishing = true;
      const payload = this.submitLabPayload();
      try {
        const response = await apiClient.reviewLabResults(payload);
        if (response.success) {
          this.publishing = false;
          this.$emit("refetch-requests");
          this.$toast.success("Lab results reviewed and published successfully");
        } else {
          this.publishing = false;
          this.$toast.error("Failed to submit lab results");
        }
      } catch (error) {
        this.publishing = false;
        this.$toast.error("Failed to submit lab results: " + error.message);
        console.error("Submission error:", error);
      }
    },
    extractIds() {
      const items = this.requests.filter(item => item.status?.code === "ST-003");
      return items.map(item => item.id);
    },
    formattedTimestamp(dateString) {
      if (!dateString) return null;
      const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      };
      return new Date(dateString).toLocaleString("en-US", options);
    },
    async downloadFile(fileUrl) {
      try {
        const response = await axios.get(fileUrl, { responseType: "blob" });
        let fileName = fileUrl.split("/").pop().split("?")[0];
        const contentDisposition = response.headers["content-disposition"];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match) fileName = match[1];
        }
        const blob = new Blob([response.data]);
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error downloading file:", error);
        this.$toast.error("Failed to download file.");
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
